import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  DocumentTextIcon,
  PhotoIcon,
  DocumentIcon,
  CodeBracketIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CloudArrowUpIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
// @ts-ignore
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import MarkdownContent from '../MarkdownContent';
import ImagePreview from '../common/ImagePreview';
import { i18nService } from '../../services/i18n';
import { formatFileSize } from '../../lib/fileSize';
import { createLatestRequestGuard, createTaskQueue, desktopThumbnailCacheKey } from '../../lib/deliverableLoading';
import { fetchDeliverableArtifactBlob, getDeliverableArtifactDownloadUrl } from '../../services/deliverableArtifacts';
import { getTargetClient, relayService } from '../../services/relay';
import type { CoworkMessage, CoworkExecutionMode, CoworkSession } from '../../types/cowork';
import {
  extractMarkdownLinkSources,
  normalizeDeliverableLinkPath,
} from '../../lib/deliverableLinks';

// ────────────────────────────────────────────
// Auth helper — reuse token from webAdapter
// ────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('boclaw_token');
  const userId = localStorage.getItem('boclaw_user_id');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (userId) headers['X-Boclaw-User-Id'] = userId;
  return headers;
}

// ────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────

const SANDBOX_WORKSPACE_GUEST_ROOT = '/workspace/project';

const WRITE_TOOLS = new Set(['Write', 'write']);
const EDIT_TOOLS = new Set(['Edit', 'edit', 'MultiEdit', 'multiEdit']);
const BASH_TOOLS = new Set(['Bash', 'bash']);

/**
 * Extract file paths created by a Bash command.
 * Recognises: > / >> redirection, tee, cp, mv, touch.
 * Only absolute paths (starting with /) are accepted.
 * Paths containing `$` (variable expansion) or `/dev/null` are excluded.
 */
const extractFilePathsFromBashCommand = (command: string | undefined): string[] => {
  if (!command) return [];
  const paths: string[] = [];

  const addIfValid = (p: string) => {
    const cleaned = p.replace(/['";]+$/g, '').trim();
    if (cleaned.startsWith('/') && !cleaned.includes('$') && cleaned !== '/dev/null') {
      paths.push(cleaned);
    }
  };

  // > or >> redirection
  const redirectRe = />{1,2}\s*([^\s|&;]+)/g;
  let m: RegExpExecArray | null;
  while ((m = redirectRe.exec(command)) !== null) addIfValid(m[1]);

  // tee [-a] <path>
  const teeRe = /\btee\s+(?:-a\s+)?([^\s|&;]+)/g;
  while ((m = teeRe.exec(command)) !== null) addIfValid(m[1]);

  // cp <src> <dest>  — last arg is the destination
  const cpRe = /\bcp\s+(?:-[^\s]+\s+)*([^\s]+)\s+([^\s|&;]+)/g;
  while ((m = cpRe.exec(command)) !== null) addIfValid(m[2]);

  // mv <src> <dest>
  const mvRe = /\bmv\s+(?:-[^\s]+\s+)*([^\s]+)\s+([^\s|&;]+)/g;
  while ((m = mvRe.exec(command)) !== null) addIfValid(m[2]);

  // touch <path> [<path> ...]
  const touchRe = /\btouch\s+((?:(?:-[^\s]+)\s+)*)(.*)/g;
  while ((m = touchRe.exec(command)) !== null) {
    const args = m[2].split(/\s+/).filter(Boolean);
    for (const arg of args) {
      if (arg.startsWith('-')) continue;
      addIfValid(arg);
    }
  }

  return [...new Set(paths)];
};

const IMAGE_EXTS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.bmp', '.ico']);
const DOC_EXTS = new Set(['.pdf', '.docx', '.xlsx', '.pptx', '.doc', '.xls', '.ppt']);
const MARKDOWN_EXTS = new Set(['.md', '.mdx', '.markdown']);
const HTML_EXTS = new Set(['.html', '.htm']);

const CODE_HIGHLIGHT_CHAR_LIMIT = 20000;

/** Map file extension → Prism language identifier */
const EXT_TO_LANGUAGE: Record<string, string> = {
  '.js': 'javascript', '.jsx': 'jsx', '.ts': 'typescript', '.tsx': 'tsx',
  '.py': 'python', '.go': 'go', '.rs': 'rust', '.java': 'java',
  '.c': 'c', '.cpp': 'cpp', '.h': 'c', '.hpp': 'cpp',
  '.rb': 'ruby', '.php': 'php', '.swift': 'swift', '.kt': 'kotlin',
  '.scala': 'scala', '.vue': 'markup', '.svelte': 'markup',
  '.css': 'css', '.scss': 'scss', '.less': 'less', '.sass': 'sass',
  '.html': 'html', '.htm': 'html', '.xml': 'xml', '.svg': 'svg',
  '.json': 'json', '.yaml': 'yaml', '.yml': 'yaml', '.toml': 'toml',
  '.sh': 'bash', '.bash': 'bash', '.zsh': 'bash', '.fish': 'bash',
  '.sql': 'sql', '.graphql': 'graphql', '.gql': 'graphql',
  '.lua': 'lua', '.r': 'r', '.R': 'r',
  '.ex': 'elixir', '.exs': 'elixir', '.clj': 'clojure',
  '.hs': 'haskell', '.ml': 'ocaml', '.dart': 'dart',
  '.dockerfile': 'docker', '.tf': 'hcl', '.proto': 'protobuf',
  '.ini': 'ini', '.cfg': 'ini', '.conf': 'ini',
  '.makefile': 'makefile', '.cmake': 'cmake',
  '.diff': 'diff', '.patch': 'diff',
  '.md': 'markdown', '.mdx': 'markdown',
};

const CODE_EXTS = new Set(Object.keys(EXT_TO_LANGUAGE));

const DATA_EXTS = new Set(['.csv', '.tsv', '.sql', '.graphql', '.gql', '.xml', '.xls', '.xlsx']);

type FileFilterCategory = 'all' | 'doc' | 'code' | 'data' | 'image' | 'other';

const FILTER_CHIPS: { key: FileFilterCategory; labelKey: string }[] = [
  { key: 'all', labelKey: 'coworkDeliverablesFilterAll' },
  { key: 'doc', labelKey: 'coworkDeliverablesFilterDoc' },
  { key: 'code', labelKey: 'coworkDeliverablesFilterCode' },
  { key: 'data', labelKey: 'coworkDeliverablesFilterData' },
  { key: 'image', labelKey: 'coworkDeliverablesFilterImage' },
  { key: 'other', labelKey: 'coworkDeliverablesFilterOther' },
];

/** Classify a file path into a filter category */
const getFilterCategory = (filePath: string): FileFilterCategory => {
  const ext = getExt(filePath);
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (DATA_EXTS.has(ext)) return 'data';
  if (DOC_EXTS.has(ext) || MARKDOWN_EXTS.has(ext)) return 'doc';
  if (CODE_EXTS.has(ext)) return 'code';
  return 'other';
};

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

type FileBadge = 'Upload' | 'Write' | 'Edit';

interface DeliverablePanelProps {
  messages: CoworkMessage[];
  cwd?: string;
  executionMode?: CoworkExecutionMode;
  sessionId?: string;
  origin?: CoworkSession['origin'];
  /** When set, auto-select and preview the file whose path matches */
  highlightPath?: string | null;
  /** Deliverable paths registered via sandbox IPC (auto-detect + explicit + scan). */
  registeredDeliverables?: string[];
}

interface DeliverableFile {
  path: string;
  hostPath: string;
  badge: FileBadge;
  fileName: string;
}

interface FilePreview {
  type: 'image' | 'text' | 'binary';
  dataUrl?: string;
  content?: string;
  fileName?: string;
  size?: number;
  loading: boolean;
  error?: string;
}

// ────────────────────────────────────────────
// Helpers (unchanged logic)
// ────────────────────────────────────────────

const getExt = (filePath: string): string => {
  const dot = filePath.lastIndexOf('.');
  return dot >= 0 ? filePath.slice(dot).toLowerCase() : '';
};

const getFileIcon = (filePath: string) => {
  const ext = getExt(filePath);
  if (IMAGE_EXTS.has(ext)) return PhotoIcon;
  if (DOC_EXTS.has(ext)) return DocumentIcon;
  if (CODE_EXTS.has(ext)) return CodeBracketIcon;
  return DocumentTextIcon;
};

const badgeStyles: Record<FileBadge, string> = {
  Upload: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  Write: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  Edit: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
};

const badgeIconColor: Record<FileBadge, string> = {
  Upload: 'text-purple-500',
  Write: 'text-blue-500',
  Edit: 'text-amber-500',
};

const mapGuestPathToHost = (filePath: string, cwd?: string): string => {
  if (!cwd) return filePath;
  const normalized = filePath.replace(/\\/g, '/');
  if (
    normalized === SANDBOX_WORKSPACE_GUEST_ROOT ||
    normalized.startsWith(`${SANDBOX_WORKSPACE_GUEST_ROOT}/`)
  ) {
    const relative = normalized.slice(SANDBOX_WORKSPACE_GUEST_ROOT.length);
    const cleanCwd = cwd.replace(/[\\/]+$/, '');
    return relative ? `${cleanCwd}${relative}` : cleanCwd;
  }
  return filePath;
};

const isInWorkspace = (filePath: string, cwd?: string, executionMode?: CoworkExecutionMode): boolean => {
  const normalized = filePath.replace(/\\/g, '/');
  if (executionMode === 'sandbox') {
    // Accept any path under /workspace/ — sandbox is an isolated container so all workspace paths are safe
    if (normalized === '/workspace' || normalized.startsWith('/workspace/')) return true;
    // Accept relative paths under .cowork-temp/ (uploaded files in sandbox mode)
    if (normalized.startsWith('.cowork-temp/') || normalized.startsWith('./.cowork-temp/')) return true;
    return false;
  }
  if (!cwd) return true;
  const cleanCwd = cwd.replace(/[\\/]+$/, '');
  return normalized === cleanCwd || normalized.startsWith(`${cleanCwd}/`);
};

/** Convert a relative or guest path to an absolute container path */
const toContainerPath = (filePath: string): string => {
  const normalized = filePath.replace(/\\/g, '/');
  // Already an absolute container path
  if (normalized.startsWith('/')) return normalized;
  // Relative path → prepend /workspace/project/
  return `${SANDBOX_WORKSPACE_GUEST_ROOT}/${normalized.replace(/^\.\//, '')}`;
};

const parseUploadedFiles = (messages: CoworkMessage[]): string[] => {
  const paths: string[] = [];
  for (const msg of messages) {
    if (msg.type !== 'user' || !msg.content) continue;
    const lines = msg.content.split('\n');
    for (const line of lines) {
      const match = line.match(/^输入文件[:：]\s*(.+)$/);
      if (match) {
        paths.push(match[1].trim());
      }
    }
  }
  return paths;
};

/** Detect file category for preview routing */
const getFileCategory = (filePath: string): 'code' | 'markdown' | 'html' | 'image' | 'binary' => {
  const ext = getExt(filePath);
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (MARKDOWN_EXTS.has(ext)) return 'markdown';
  if (HTML_EXTS.has(ext)) return 'html';
  if (CODE_EXTS.has(ext)) return 'code';
  // Plain text files that don't match any known code ext – treat as code (no highlight)
  return 'binary';
};

// ────────────────────────────────────────────
// Module-level thumbnail cache
// ────────────────────────────────────────────

const thumbnailCache = new Map<string, string>();
const desktopThumbnailQueue = createTaskQueue(2);

// ────────────────────────────────────────────
// Local Components
// ────────────────────────────────────────────

/** Horizontal filter chip bar for file type categories */
const FilterChips: React.FC<{
  active: FileFilterCategory;
  counts: Record<FileFilterCategory, number>;
  onChange: (cat: FileFilterCategory) => void;
}> = ({ active, counts, onChange }) => (
  <div className="flex flex-wrap gap-1.5 px-3 py-2">
    {FILTER_CHIPS.map(({ key, labelKey }) => {
      const isActive = active === key;
      const count = counts[key];
      return (
        <button
          key={key}
          type="button"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors select-none ${
            isActive
              ? 'bg-emerald-500 text-white'
              : 'border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-emerald-500 hover:text-emerald-500 dark:hover:border-emerald-400 dark:hover:text-emerald-400'
          }`}
          onClick={() => onChange(key)}
        >
          <span>{i18nService.t(labelKey)}</span>
          <span className={`text-[10px] ${isActive ? 'text-white/80' : ''}`}>{count}</span>
        </button>
      );
    })}
  </div>
);

/** Lazy-loaded image thumbnail with module-level cache */
const FileThumbnail: React.FC<{ hostPath: string; alt: string; readUrl?: string; desktopTargetClientId?: string | null; desktop?: boolean; userId: string | null }> = ({ hostPath, alt, readUrl, desktopTargetClientId, desktop, userId }) => {
  const cacheKey = desktop
    ? desktopTargetClientId ? desktopThumbnailCacheKey(userId, desktopTargetClientId, hostPath) : null
    : userId ? JSON.stringify(['web', userId, readUrl || hostPath]) : null;
  const [src, setSrc] = useState<string | null>(() => cacheKey ? thumbnailCache.get(cacheKey) ?? null : null);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(!desktop || Boolean(src));
  const thumbnailRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!desktop || visible || src || !desktopTargetClientId) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '100px' });
    if (thumbnailRef.current) observer.observe(thumbnailRef.current);
    return () => observer.disconnect();
  }, [desktop, desktopTargetClientId, src, visible]);

  useEffect(() => {
    let cancelled = false;
    const cached = cacheKey ? thumbnailCache.get(cacheKey) : null;
    if (cached) { setError(false); setSrc(cached); return; }
    setSrc(null);
    setError(false);
    if (desktop && (!desktopTargetClientId || !visible)) return;
    (async () => {
      try {
        let dataUrl: string;
        if (desktopTargetClientId) {
          const image = await desktopThumbnailQueue.run(async () => {
            if (cancelled) return null;
            await relayService.ensureConnected();
            if (cancelled) return null;
            return relayService.readImage(desktopTargetClientId, hostPath, 160, 75);
          });
          if (!image || cancelled) return;
          if (!image.content || !image.mimeType) throw new Error('Empty image response');
          dataUrl = `data:${image.mimeType};base64,${image.content}`;
        } else {
          const url = readUrl || `/api/files/read?path=${encodeURIComponent(hostPath)}`;
          const response = await fetch(url, { headers: getAuthHeaders() });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          if (data.type !== 'image' || !data.dataUrl) throw new Error('Invalid image response');
          dataUrl = data.dataUrl;
        }
        if (!cancelled) {
          if (cacheKey) thumbnailCache.set(cacheKey, dataUrl);
          setSrc(dataUrl);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [hostPath, cacheKey, readUrl, desktop, desktopTargetClientId, visible]);

  return (
    <span ref={thumbnailRef} className="h-6 w-6 shrink-0 flex items-center justify-center">
      {error || !src
        ? <PhotoIcon className="h-5 w-5 text-purple-500" />
        : <img src={src} alt={alt} className="h-6 w-6 rounded object-cover" />}
    </span>
  );
};

/** Collapsible category group header */
const CategoryGroup: React.FC<{
  icon: React.ElementType;
  iconColor: string;
  label: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ icon: Icon, iconColor, label, count, expanded, onToggle, children }) => (
  <div className="mb-1">
    <button
      type="button"
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-black/5 dark:hover:bg-white/5 transition-colors select-none"
      onClick={onToggle}
    >
      {expanded ? (
        <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <ChevronRightIcon className="h-3.5 w-3.5 shrink-0" />
      )}
      <Icon className={`h-3.5 w-3.5 shrink-0 ${iconColor}`} />
      <span className="flex-1">{label}</span>
      <span className="text-[10px] tabular-nums opacity-60">{count}</span>
    </button>
    {expanded && (
      <div className="mt-0.5 space-y-0.5">
        {children}
      </div>
    )}
  </div>
);

/** Single file card in the list */
const FileCard: React.FC<{
  file: DeliverableFile;
  isSelected: boolean;
  onClick: () => void;
  onDownload: (e: React.MouseEvent) => void;
  readUrl?: string;
  desktopTargetClientId?: string | null;
  desktop?: boolean;
  userId: string | null;
}> = ({ file, isSelected, onClick, onDownload, readUrl, desktopTargetClientId, desktop, userId }) => {
  const ext = getExt(file.path);
  const isImage = IMAGE_EXTS.has(ext);
  const FileIcon = getFileIcon(file.path);

  return (
    <div
      className={`group w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-sm dark:text-claude-darkText text-claude-text transition-colors cursor-pointer ${
        isSelected
          ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover ring-1 ring-emerald-500/30'
          : 'dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
      }`}
      onClick={onClick}
    >
      {isImage ? (
        <FileThumbnail key={`${userId}:${desktopTargetClientId || 'web'}:${file.hostPath}`} hostPath={file.hostPath} alt={file.fileName} readUrl={readUrl} desktopTargetClientId={desktopTargetClientId} desktop={desktop} userId={userId} />
      ) : (
        <FileIcon className={`h-4 w-4 shrink-0 ${badgeIconColor[file.badge]}`} />
      )}
      <span className="truncate flex-1 min-w-0">{file.fileName}</span>
      <span
        className={`shrink-0 text-[10px] font-semibold px-1.5 py-0 rounded-full border ${badgeStyles[file.badge]}`}
      >
        {file.badge}
      </span>
      <button
        type="button"
        className="shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
        onClick={onDownload}
        title={i18nService.t('coworkDeliverablesDownload')}
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
      </button>
    </div>
  );
};

// ────────────────────────────────────────────
// Preview Viewers
// ────────────────────────────────────────────

const SYNTAX_STYLE = { margin: 0, borderRadius: 0, background: '#282c34' };
const CODE_PRE_STYLE: React.CSSProperties = {
  backgroundColor: '#282c34',
  color: '#e5e7eb',
  margin: 0,
  borderRadius: 0,
  padding: '12px 16px',
  fontFamily: "'SF Mono', 'Fira Code', Menlo, Monaco, 'Courier New', monospace",
  whiteSpace: 'pre',
  overflowX: 'auto',
};

/** Code with syntax highlighting (or plain <pre> fallback) */
const CodePreview: React.FC<{ content: string; filePath: string }> = ({ content, filePath }) => {
  const ext = getExt(filePath);
  const language = EXT_TO_LANGUAGE[ext] || 'text';
  const tooLarge = content.length > CODE_HIGHLIGHT_CHAR_LIMIT;

  if (tooLarge) {
    return (
      <div className="overflow-auto h-full text-[13px] leading-6" style={{ backgroundColor: '#282c34' }}>
        <pre style={CODE_PRE_STYLE}>{content}</pre>
      </div>
    );
  }

  return (
    <div className="overflow-auto h-full text-[13px]" style={{ backgroundColor: '#282c34' }}>
      <SyntaxHighlighter
        style={oneDark}
        language={language}
        showLineNumbers
        customStyle={SYNTAX_STYLE}
      >
        {content}
      </SyntaxHighlighter>
    </div>
  );
};

/** Markdown rendered via MarkdownContent */
const MarkdownPreview: React.FC<{ content: string }> = ({ content }) => (
  <div className="overflow-auto h-full p-4 dark:bg-claude-darkSurface bg-white">
    <MarkdownContent content={content} />
  </div>
);

/** HTML rendered in a sandboxed iframe */
const HtmlPreview: React.FC<{ content: string }> = ({ content }) => (
  <iframe
    srcDoc={content}
    sandbox="allow-scripts"
    className="w-full h-full border-0 bg-white"
    title="HTML Preview"
  />
);

/** Binary / unsupported file info card */
const BinaryInfo: React.FC<{
  fileName: string;
  size?: number;
  onDownload: () => void;
}> = ({ fileName, size, onDownload }) => (
  <div className="flex flex-col items-center justify-center h-full gap-2 dark:text-claude-darkTextSecondary text-claude-textSecondary">
    <DocumentIcon className="h-12 w-12 opacity-30" />
    <div className="text-sm font-medium dark:text-claude-darkText text-claude-text">{fileName}</div>
    {size !== undefined && (
      <div className="text-xs">{formatFileSize(size)}</div>
    )}
    <div className="text-xs">{i18nService.t('coworkDeliverablesNoPreview')}</div>
    <button
      type="button"
      className="mt-1 px-3 py-1 text-xs rounded-md bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors"
      onClick={onDownload}
    >
      {i18nService.t('coworkDeliverablesDownload')}
    </button>
  </div>
);

// ────────────────────────────────────────────
// FilePreviewModal
// ────────────────────────────────────────────

const FilePreviewModal: React.FC<{
  file: DeliverableFile;
  preview: FilePreview;
  onClose: () => void;
  onDownload: () => void;
}> = ({ file, preview, onClose, onDownload }) => {
  // Escape key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const category = getFileCategory(file.path);

  const renderViewer = () => {
    if (preview.loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-500 border-t-transparent" />
        </div>
      );
    }
    if (preview.error) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-red-500">
          {preview.error}
        </div>
      );
    }

    // Image
    if (preview.type === 'image' && preview.dataUrl) {
      return <ImagePreview dataUrl={preview.dataUrl} fileName={file.fileName} />;
    }

    // Text-based
    if (preview.type === 'text' && preview.content !== undefined) {
      if (category === 'markdown') return <MarkdownPreview content={preview.content} />;
      if (category === 'html') return <HtmlPreview content={preview.content} />;
      if (category === 'code') return <CodePreview content={preview.content} filePath={file.path} />;
      // Fallback for unknown text types — try code preview
      return <CodePreview content={preview.content} filePath={file.path} />;
    }

    // Binary / unknown
    return (
      <BinaryInfo
        fileName={preview.fileName || file.fileName}
        size={preview.size}
        onDownload={onDownload}
      />
    );
  };

  return (
    <div className="absolute inset-0 z-10 flex flex-col dark:bg-claude-darkSurface bg-claude-surface">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <span className="text-sm font-medium dark:text-claude-darkText text-claude-text truncate flex-1 min-w-0">
          {file.fileName}
        </span>
        <span
          className={`shrink-0 text-[10px] font-semibold px-1.5 py-0 rounded-full border ${badgeStyles[file.badge]}`}
        >
          {file.badge}
        </span>
        <button
          type="button"
          className="shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          onClick={onDownload}
          title={i18nService.t('coworkDeliverablesDownload')}
        >
          <ArrowDownTrayIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
        </button>
        <button
          type="button"
          className="shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          onClick={onClose}
          title="Close"
        >
          <XMarkIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
        </button>
      </div>

      {/* Viewer area */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {renderViewer()}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────

const DeliverablePanel: React.FC<DeliverablePanelProps> = ({ messages, cwd, executionMode, sessionId, origin, highlightPath, registeredDeliverables }) => {
  const [selectedFile, setSelectedFile] = useState<DeliverableFile | null>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const desktopTargetClientId = origin === 'desktop' && sessionId ? getTargetClient(sessionId) : null;
  const userId = localStorage.getItem('boclaw_user_id');
  const previewGuard = useMemo(() => createLatestRequestGuard(), []);
  const prevHighlightRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    previewGuard.invalidate();
    prevHighlightRef.current = undefined;
    setSelectedFile(null);
    setPreview(null);
  }, [sessionId, origin, userId, previewGuard]);

  // ── File extraction (unchanged logic) ──
  const files = useMemo<DeliverableFile[]>(() => {
    const seen = new Map<string, DeliverableFile>();

    const uploadPaths = parseUploadedFiles(messages);
    for (const filePath of uploadPaths) {
      // In sandbox mode, uploaded files have host-side staging paths (e.g. /Users/.../uploads/...)
      // that don't match the container workspace. Always show uploaded files — they're from user messages.
      if (executionMode !== 'sandbox' && !isInWorkspace(filePath, cwd, executionMode)) continue;
      const hostPath = executionMode === 'sandbox' ? mapGuestPathToHost(filePath, cwd) : filePath;
      const fileName = filePath.split('/').pop() || filePath;
      seen.set(filePath, { path: filePath, hostPath, badge: 'Upload', fileName });
    }

    for (const msg of messages) {
      if (msg.type !== 'tool_use' || !msg.metadata?.toolName) continue;
      const toolName = msg.metadata.toolName;
      const toolInput = msg.metadata.toolInput as Record<string, unknown> | undefined;

      // Collect file paths to process: Write/Edit have a single file_path, Bash may produce multiple
      const entries: { filePath: string; badge: FileBadge }[] = [];

      if (WRITE_TOOLS.has(toolName)) {
        const fp = toolInput?.file_path as string | undefined;
        if (fp) entries.push({ filePath: fp, badge: 'Write' });
      } else if (EDIT_TOOLS.has(toolName)) {
        const fp = toolInput?.file_path as string | undefined;
        if (fp) entries.push({ filePath: fp, badge: 'Edit' });
      } else if (BASH_TOOLS.has(toolName)) {
        const bashPaths = extractFilePathsFromBashCommand(toolInput?.command as string | undefined);
        for (const bp of bashPaths) entries.push({ filePath: bp, badge: 'Write' });
      }

      for (const { filePath, badge } of entries) {
        if (!isInWorkspace(filePath, cwd, executionMode)) continue;
        const hostPath = executionMode === 'sandbox' ? mapGuestPathToHost(filePath, cwd) : filePath;
        const fileName = filePath.split('/').pop() || filePath;
        const existing = seen.get(filePath);
        if (existing?.badge === 'Upload') continue;
        seen.set(filePath, { path: filePath, hostPath, badge, fileName });
      }
    }

    // ── Source 3: IPC-registered deliverables from the sandbox ──
    // Catches Bash/scripted products that aren't visible via Write/Edit
    // tool calls. Dedup against the two sources above (Upload / message parsing).
    if (registeredDeliverables) {
      for (const filePath of registeredDeliverables) {
        if (seen.has(filePath)) continue;
        if (!isInWorkspace(filePath, cwd, executionMode)) continue;
        const hostPath = executionMode === 'sandbox' ? mapGuestPathToHost(filePath, cwd) : filePath;
        const fileName = filePath.split('/').pop() || filePath;
        seen.set(filePath, { path: filePath, hostPath, badge: 'Write', fileName });
      }
    }

    // ── Source 4: scan assistant messages for markdown links / embedded paths ──
    // Fallback for scripts that produce pptx/docx/xlsx/pdf/etc. and only surface
    // the final path as a markdown link in the model's closing summary, e.g.
    //   文件位置: [ai-intro.pptx](ai-intro.pptx)
    //   ![chart](images/chart.png)
    // Covers both `![]()` image markdown and plain `[text](path)` link markdown.
    const DELIVERABLE_LINK_EXTS = new Set([
      '.pptx', '.docx', '.xlsx', '.pdf', '.csv', '.tsv',
      '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.bmp',
      '.mp4', '.mp3', '.wav', '.mov', '.avi',
      '.zip', '.tar', '.gz',
      '.html', '.htm',
    ]);
    for (const msg of messages) {
      if (msg.type !== 'assistant') continue;
      const content = msg.content || '';
      const workspaceRoot = executionMode === 'sandbox'
        ? SANDBOX_WORKSPACE_GUEST_ROOT
        : cwd;
      if (!workspaceRoot) continue;
      for (const src of extractMarkdownLinkSources(content)) {
        const normalizedSrc = normalizeDeliverableLinkPath(src, workspaceRoot);
        if (!normalizedSrc) continue;
        // Only accept paths with a known deliverable extension
        const lastDot = normalizedSrc.lastIndexOf('.');
        if (lastDot < 0) continue;
        const ext = normalizedSrc.slice(lastDot).toLowerCase();
        if (!DELIVERABLE_LINK_EXTS.has(ext)) continue;
        const containerPath = normalizedSrc;
        if (!isInWorkspace(containerPath, cwd, executionMode)) continue;
        if (seen.has(containerPath)) continue;
        const hostPath = executionMode === 'sandbox' ? mapGuestPathToHost(containerPath, cwd) : containerPath;
        const fileName = containerPath.split('/').pop() || containerPath;
        seen.set(containerPath, { path: containerPath, hostPath, badge: 'Write', fileName });
      }
    }

    return Array.from(seen.values());
  }, [messages, cwd, executionMode, registeredDeliverables]);

  const [activeFilter, setActiveFilter] = useState<FileFilterCategory>('all');
  const [uploadExpanded, setUploadExpanded] = useState(true);
  const [generatedExpanded, setGeneratedExpanded] = useState(true);

  const filterCounts = useMemo(() => {
    const counts: Record<FileFilterCategory, number> = { all: files.length, doc: 0, code: 0, data: 0, image: 0, other: 0 };
    for (const f of files) {
      counts[getFilterCategory(f.path)]++;
    }
    return counts;
  }, [files]);

  const filteredFiles = useMemo(() => {
    if (activeFilter === 'all') return files;
    return files.filter(f => getFilterCategory(f.path) === activeFilter);
  }, [files, activeFilter]);

  /** Split filtered files into upload vs generated groups */
  const { uploadFiles, generatedFiles } = useMemo(() => {
    const uploadFiles: DeliverableFile[] = [];
    const generatedFiles: DeliverableFile[] = [];
    for (const f of filteredFiles) {
      if (f.badge === 'Upload') {
        uploadFiles.push(f);
      } else {
        generatedFiles.push(f);
      }
    }
    return { uploadFiles, generatedFiles };
  }, [filteredFiles]);

  // ── URL helpers for sandbox vs local mode ──
  const buildReadUrl = useCallback((file: DeliverableFile): string => {
    if (executionMode === 'sandbox' && sessionId) {
      // Uploaded files exist on the host staging directory — read directly from host
      if (file.badge === 'Upload') {
        return `/api/files/read?path=${encodeURIComponent(file.path)}`;
      }
      // AI-generated files only exist inside the container — read via proxy
      const containerPath = toContainerPath(file.path);
      return `/api/cowork/sessions/${sessionId}/files/read?path=${encodeURIComponent(containerPath)}`;
    }
    return `/api/files/read?path=${encodeURIComponent(file.hostPath)}`;
  }, [executionMode, sessionId]);

  const buildDownloadUrl = useCallback((file: DeliverableFile): string => {
    if (executionMode === 'sandbox' && sessionId) {
      if (file.badge === 'Upload') {
        return `/api/files/download?path=${encodeURIComponent(file.path)}`;
      }
      const containerPath = toContainerPath(file.path);
      return `/api/cowork/sessions/${sessionId}/files/download?path=${encodeURIComponent(containerPath)}`;
    }
    return `/api/files/download?path=${encodeURIComponent(file.hostPath)}`;
  }, [executionMode, sessionId]);

  // ── Helpers: try a URL, fallback to host path if container proxy fails ──
  const fetchFileWithFallback = useCallback(async (file: DeliverableFile): Promise<Response> => {
    const primaryUrl = buildReadUrl(file);
    const headers = getAuthHeaders();
    const resp = await fetch(primaryUrl, { headers });
    if (resp.ok) return resp;

    // For sandbox generated files: if container proxy fails, try host path (file may have been synced back)
    if (executionMode === 'sandbox' && file.badge !== 'Upload' && file.hostPath) {
      const fallbackUrl = `/api/files/read?path=${encodeURIComponent(file.hostPath)}`;
      const fallbackResp = await fetch(fallbackUrl, { headers });
      if (fallbackResp.ok) return fallbackResp;
    }
    return resp; // Return original failed response
  }, [buildReadUrl, executionMode]);

  // ── Handlers ──
  const handleFileClick = useCallback(async (file: DeliverableFile) => {
    const requestVersion = previewGuard.next();
    setSelectedFile(file);
    setPreview({ type: 'text', loading: true });

    try {
      if (origin === 'desktop') {
        if (!desktopTargetClientId) throw new Error(i18nService.t('relayDesktopOffline'));
        await relayService.ensureConnected();
        if (IMAGE_EXTS.has(getExt(file.path))) {
          const image = await relayService.readImage(desktopTargetClientId, file.path, 2400, 90);
          if (!image.content || !image.mimeType) throw new Error('Desktop image response is empty');
          if (previewGuard.isCurrent(requestVersion)) {
            setPreview({ type: 'image', loading: false, dataUrl: `data:${image.mimeType};base64,${image.content}` });
          }
        } else {
          const result = await relayService.readFile(desktopTargetClientId, file.path);
          if (previewGuard.isCurrent(requestVersion)) {
            setPreview(result.encoding === 'utf-8'
              ? { type: 'text', loading: false, content: result.content, fileName: file.fileName }
              : { type: 'binary', loading: false, fileName: file.fileName, size: result.size });
          }
        }
        return;
      }
      const resp = await fetchFileWithFallback(file);
      if (!previewGuard.isCurrent(requestVersion)) return;
      if (!resp.ok) {
        // 409 SESSION_NOT_RUNNING：沙箱空闲被暂停回收（onTimeout=pause，文件系统快照保留），
        // 文件没丢，但读取要求会话在 host 侧处于运行态。以前这里一律报
        // "Failed to load file (container may have stopped)"，让用户以为文件没了——
        // 其实发一条消息继续会话触发 resume 即可恢复访问。这里把这种情况单独说清楚。
        const recoverable = resp.status === 409;
        setPreview({
          type: 'binary',
          loading: false,
          error: recoverable
            ? i18nService.t('coworkDeliverableSandboxPaused')
            : i18nService.t('coworkDeliverableDownloadFailed'),
        });
        return;
      }
      const data = await resp.json();
      if (previewGuard.isCurrent(requestVersion)) setPreview({ ...data, loading: false });
    } catch (error) {
      if (!previewGuard.isCurrent(requestVersion)) return;
      if (origin === 'desktop' && error instanceof Error && error.message.startsWith('FILE_TOO_LARGE:')) {
        setPreview({ type: 'binary', loading: false, fileName: file.fileName });
      } else {
        setPreview({ type: 'binary', loading: false, error: origin === 'desktop' && error instanceof Error
          ? error.message : i18nService.t('coworkDeliverableDownloadFailed') });
      }
    }
  }, [desktopTargetClientId, fetchFileWithFallback, origin, previewGuard]);

  const handleDownload = useCallback(async (file: DeliverableFile) => {
    const previewVersion = previewGuard.current();
    if (origin === 'desktop') {
      try {
        if (!desktopTargetClientId) throw new Error(i18nService.t('relayDesktopOffline'));
        await relayService.ensureConnected();
        const blob = await relayService.downloadFile(desktopTargetClientId, file.path);
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } catch (error) {
        if (!previewGuard.isCurrent(previewVersion)) return;
        setSelectedFile(file);
        setPreview({ type: 'binary', loading: false, error: error instanceof Error
          ? error.message : i18nService.t('coworkDeliverableDownloadFailed') });
      }
      return;
    }
    const headers = getAuthHeaders();

    // WEB-09: prefer the Host-owned artifact announced via
    // cowork.stream.deliverableArtifact (survives sandbox pause). The endpoint
    // needs the Bearer header, so fetch+blob; any failure falls through.
    if (executionMode === 'sandbox' && sessionId && file.badge !== 'Upload') {
      const artifactUrl = getDeliverableArtifactDownloadUrl(sessionId, toContainerPath(file.path));
      const artifactBlob = artifactUrl ? await fetchDeliverableArtifactBlob(artifactUrl, headers) : null;
      if (artifactBlob) {
        const blobUrl = URL.createObjectURL(artifactBlob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        return;
      }
    }

    // 沙箱生成文件优先走「签名 URL + 浏览器原生下载」：
    // 1) 大文件不用先读进内存做 blob；2) 得到的 URL 自带短时令牌，用户复制到新标签页 /
    //    右键"链接另存为"也能打开——修 48ac497 加鉴权后原生导航 401 的回归。
    // 签名失败（旧后端/网络）再退回下面的 fetch+blob 路径。
    if (executionMode === 'sandbox' && sessionId && file.badge !== 'Upload') {
      try {
        const containerPath = toContainerPath(file.path);
        const signResp = await fetch(
          `/api/cowork/sessions/${sessionId}/files/sign?path=${encodeURIComponent(containerPath)}`,
          { headers },
        );
        if (signResp.ok) {
          const { downloadUrl } = await signResp.json() as { downloadUrl?: string };
          if (downloadUrl) {
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = file.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
          }
        }
      } catch { /* fall through to blob download */ }
    }

    // Build download URLs to try
    const urls: string[] = [buildDownloadUrl(file)];
    // Fallback: for sandbox generated files, try host path too
    if (executionMode === 'sandbox' && file.badge !== 'Upload' && file.hostPath) {
      urls.push(`/api/files/download?path=${encodeURIComponent(file.hostPath)}`);
    }

    let sawPaused = false;
    for (const url of urls) {
      try {
        const resp = await fetch(url, { headers });
        if (!resp.ok) {
          // 记住"沙箱已暂停"，好在全部失败后给可恢复提示而不是笼统的失败
          if (resp.status === 409) sawPaused = true;
          continue;
        }
        const blob = await resp.blob();
        if (blob.size === 0) continue;
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        return; // Success
      } catch {
        continue; // Try next URL
      }
    }
    // 全部尝试失败。以前这里兜底 window.open(downloadUrl)，但沙箱文件端点现在要求
    // Authorization 头，原生导航带不了头，兜底只会打开一个 401 的 JSON 错误页——
    // 比直接告诉用户失败更糟。改为复用预览区的错误提示。
    if (!previewGuard.isCurrent(previewVersion)) return;
    setSelectedFile(file);
    setPreview({
      type: 'binary',
      loading: false,
      error: sawPaused
        ? i18nService.t('coworkDeliverableSandboxPaused')
        : i18nService.t('coworkDeliverableDownloadFailed'),
    });
  }, [buildDownloadUrl, desktopTargetClientId, executionMode, origin, previewGuard, sessionId]);

  const handleClosePreview = useCallback(() => {
    previewGuard.invalidate();
    setSelectedFile(null);
    setPreview(null);
  }, [previewGuard]);

  // ── Auto-select file when highlightPath changes ──
  useEffect(() => {
    if (!highlightPath || highlightPath === prevHighlightRef.current) return;
    prevHighlightRef.current = highlightPath;
    const normalizedTarget = highlightPath.replace(/^\/workspace\/project\//, '');
    const match = files.find(f => {
      const normalizedPath = f.path.replace(/^\/workspace\/project\//, '');
      const normalizedHost = f.hostPath.replace(/^\/workspace\/project\//, '');
      return f.path === highlightPath
        || f.hostPath === highlightPath
        || normalizedPath === normalizedTarget
        || normalizedHost === normalizedTarget
        || f.path.endsWith('/' + normalizedTarget)
        || f.hostPath.endsWith('/' + normalizedTarget);
    });
    if (match) {
      handleFileClick(match);
    }
  }, [highlightPath, files, handleFileClick]);

  // ── Render ──
  const showPreview = selectedFile && preview;

  return (
    <div className="relative flex flex-col h-full dark:bg-claude-darkSurface bg-claude-surface">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <DocumentTextIcon className="h-4 w-4 text-emerald-500" />
        <span className="text-sm font-medium dark:text-claude-darkText text-claude-text">
          {i18nService.t('coworkDeliverablesTitle')}
        </span>
        {files.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 border border-emerald-500/30 px-1.5 py-0 text-[10px] font-semibold">
            {files.length}
          </span>
        )}
      </div>

      {/* Filter chips */}
      {files.length > 0 && (
        <FilterChips active={activeFilter} counts={filterCounts} onChange={setActiveFilter} />
      )}

      {/* Scrollable file list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-0.5 min-h-0">
        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
            <DocumentTextIcon className="h-8 w-8 mb-2 opacity-30" />
            <span>{i18nService.t('coworkDeliverablesEmpty')}</span>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
            <DocumentTextIcon className="h-8 w-8 mb-2 opacity-30" />
            <span>{i18nService.t('coworkDeliverablesFilterEmpty')}</span>
          </div>
        ) : (
          <>
            {uploadFiles.length > 0 && (
              <CategoryGroup
                icon={CloudArrowUpIcon}
                iconColor="text-purple-500"
                label={i18nService.t('coworkDeliverablesUploadedFiles')}
                count={uploadFiles.length}
                expanded={uploadExpanded}
                onToggle={() => setUploadExpanded(v => !v)}
              >
                {uploadFiles.map(file => (
                  <FileCard
                    key={file.path}
                    file={file}
                    isSelected={selectedFile?.path === file.path}
                    onClick={() => handleFileClick(file)}
                    onDownload={(e) => { e.stopPropagation(); handleDownload(file); }}
                    readUrl={executionMode === 'sandbox' && sessionId ? buildReadUrl(file) : undefined}
                    desktop={origin === 'desktop'}
                    desktopTargetClientId={desktopTargetClientId}
                    userId={userId}
                  />
                ))}
              </CategoryGroup>
            )}
            {generatedFiles.length > 0 && (
              <CategoryGroup
                icon={CpuChipIcon}
                iconColor="text-blue-500"
                label={i18nService.t('coworkDeliverablesGeneratedFiles')}
                count={generatedFiles.length}
                expanded={generatedExpanded}
                onToggle={() => setGeneratedExpanded(v => !v)}
              >
                {generatedFiles.map(file => (
                  <FileCard
                    key={file.path}
                    file={file}
                    isSelected={selectedFile?.path === file.path}
                    onClick={() => handleFileClick(file)}
                    onDownload={(e) => { e.stopPropagation(); handleDownload(file); }}
                    readUrl={executionMode === 'sandbox' && sessionId ? buildReadUrl(file) : undefined}
                    desktop={origin === 'desktop'}
                    desktopTargetClientId={desktopTargetClientId}
                    userId={userId}
                  />
                ))}
              </CategoryGroup>
            )}
          </>
        )}
      </div>

      {/* Preview overlay – covers the panel */}
      {showPreview && (
        <FilePreviewModal
          file={selectedFile}
          preview={preview}
          onClose={handleClosePreview}
          onDownload={() => handleDownload(selectedFile)}
        />
      )}
    </div>
  );
};

export default DeliverablePanel;
